import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { detailAnswerService } from '../../services/detailAnswerService';
import { createPortal } from 'react-dom';
import MathEditor from './MathEditor';
import OptionalImageField from '../common/OptionalImageField';

const DetailAnswerDialog = ({ 
  open, 
  onClose, 
  questionId, 
  existingDetailAnswer = null,
  loadingDetailAnswer = false,
  onSave 
}) => {
  const isTemporaryQuestionId = questionId === 'new' || (typeof questionId === 'string' && questionId.startsWith('multi-row-'));
  const [answer, setAnswer] = useState('');
  const [answerImage, setAnswerImage] = useState(null);
  const [answerImagePreview, setAnswerImagePreview] = useState('');
  const [imageSource, setImageSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existingDetailAnswer) {
      const raw = existingDetailAnswer.answer;
      setAnswer(typeof raw === 'string' ? raw : (raw != null ? String(raw) : ''));
      // Use proxy URL for image preview if available, otherwise fall back to direct URL
      const imageUrl = existingDetailAnswer.answer_image_proxy_url || existingDetailAnswer.answer_image || existingDetailAnswer.answerImagePreview;
      setAnswerImagePreview(imageUrl || '');
      setAnswerImage(existingDetailAnswer.answerImage || null);
      setImageSource(existingDetailAnswer.answerImage ? 'file' : (imageUrl ? 'existing' : null));
    } else {
      setAnswer('');
      setAnswerImage(null);
      setAnswerImagePreview('');
      setImageSource(null);
    }
    setError('');
  }, [existingDetailAnswer, open, questionId]);

  const handleSave = async () => {
    if (!answer.trim() && !answerImage && !answerImagePreview) {
      setError('Please provide either an answer text or an image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let detailAnswerData;
      
      if (existingDetailAnswer && existingDetailAnswer.id && !isTemporaryQuestionId) {
        // Update existing - don't change the question field
        const updateData = {};
        
        // Only include answer if it has content
        if (answer.trim()) {
          updateData.answer = answer.trim();
        }
        
        // answer_image: keep existing if we have preview or new file; clear if user removed image
        if (answerImage) {
          // New image will be uploaded below
        } else if (answerImagePreview === '' && !answerImage) {
          updateData.answer_image = '';
        } else if (existingDetailAnswer.answer_image) {
          updateData.answer_image = existingDetailAnswer.answer_image;
        }
        
        detailAnswerData = await detailAnswerService.update(existingDetailAnswer.id, updateData);
        
        // If there's a new image to upload, update the answer_image field
        if (answerImage && detailAnswerData.id) {
          try {
            const uploadResult = await detailAnswerService.uploadImage(detailAnswerData.id, answerImage);
            // Update the detail answer with the new image URL
            await detailAnswerService.update(detailAnswerData.id, {
              answer_image: uploadResult.url
            });
            detailAnswerData.answer_image = uploadResult.url;
          } catch (imgErr) {
            console.error('Image upload failed', imgErr);
            setError('Answer saved but image upload failed');
            setLoading(false);
            return;
          }
        }
      } else if (isTemporaryQuestionId) {
        // For new questions, store the data temporarily since the question doesn't exist yet
        detailAnswerData = {
          answer: answer.trim(),
          answerImage: answerImage, // Include the actual image file
          answerImagePreview: answerImagePreview
        };
      } else {
        // Create new detail answer for existing question
        detailAnswerData = await detailAnswerService.create({
          question: questionId,
          answer: answer.trim()
        });
        
        // If there's an image to upload for new detail answer
        if (answerImage && detailAnswerData.id) {
          try {
            const uploadResult = await detailAnswerService.uploadImage(detailAnswerData.id, answerImage);
            // Update the detail answer with the image URL
            await detailAnswerService.update(detailAnswerData.id, {
              answer_image: uploadResult.url
            });
            detailAnswerData.answer_image = uploadResult.url;
          } catch (imgErr) {
            console.error('Image upload failed', imgErr);
            setError('Answer saved but image upload failed');
            setLoading(false);
            return;
          }
        }
      }

      // For new questions, include the image data in the saved data
      if (isTemporaryQuestionId) {
        onSave({
          ...detailAnswerData,
          answerImage: answerImage // Include the actual image file
        });
      } else {
        onSave(detailAnswerData);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save detail answer', err);
      setError('Failed to save detail answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImagePaste = (e) => {
    if (imageSource === 'file') return; // disabled
    
    const items = e.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            setAnswerImage(file);
            setAnswerImagePreview(URL.createObjectURL(file));
            setImageSource('paste');
          }
        }
      }
    }
  };

  const handleFileSelect = (file) => {
    if (file) {
      setAnswerImage(file);
      setAnswerImagePreview(URL.createObjectURL(file));
      setImageSource('file');
    }
  };

  const handleClearImage = () => {
    setAnswerImage(null);
    setAnswerImagePreview('');
    setImageSource(null);
  };

  return createPortal(
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={false}
      keepMounted={false}
      TransitionProps={{ timeout: { enter: 200, exit: 0 } }}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          zIndex: 1400,
        }
      }}
      BackdropProps={{
        style: { zIndex: 1399 }
      }}
    >
      <DialogTitle onClick={(e) => e.stopPropagation()}>
        {existingDetailAnswer ? 'Edit Detail Answer' : 'Add Detail Answer'}
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers onClick={(e) => e.stopPropagation()}>
        <Stack spacing={3} mt={1}>
          {loadingDetailAnswer ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
          <MathEditor
            value={answer}
            onChange={setAnswer}
            label="Answer"
            placeholder="Enter detailed answer text..."
            helperText="Use the math button (∑) to insert mathematical formulas. Formulas are added to the end for optimal performance."
          />

          <OptionalImageField
            label="Answer Image (Optional)"
            previewUrl={answerImagePreview}
            onClear={handleClearImage}
            onFileSelect={handleFileSelect}
            onPaste={(file) => {
              setAnswerImage(file);
              setAnswerImagePreview(URL.createObjectURL(file));
              setImageSource('paste');
            }}
            disabled={imageSource === 'file'}
            inputId="answer-image-file"
            imageAlt="preview"
          />

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
            </>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave} 
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>,
    document.body
  );
};

export default DetailAnswerDialog;
